import { AxiosError } from 'axios';

import {
  CreatePromptTemplateBody,
  PromptContextName,
  PromptTemplateRecord,
  PromptVariableRecord,
  UpdatePromptTemplateBody,
} from '@/types/prompts';
import { isContextName, isUnknownDottedName } from '@/utils/promptContext';

/**
 * A Prompt Template being written in the editor sheet, and the rules the
 * server applies on save (VH3_CONNECT #594, run contract "`prompts` writes"):
 * title 1–80, body 1–4,000, `{variables}` read from the body. A name on VH3's
 * list of Context Variables (`{job.reference}`, `{today}`, spec FSI #1230) is
 * valid and has no description or default; any other dotted name is refused.
 * The list comes from the lookup; while it is empty (switch off, not loaded)
 * nothing is checked here and Xano makes the same check on save.
 */
export const TITLE_MAX = 80;
export const BODY_MAX = 4000;

/** A name Make variable accepts: the fill step's own pattern. */
const VARIABLE_NAME = /^[A-Za-z0-9_-]+$/;
const DOTTED_NAME = /^[A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+)+$/;
/** A `{variable}` or a dotted `{job.reference}`, as the fill step splits them. */
const SLOT = /\{([A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+)*)\}/g;

export const NOT_CONTEXT_NOTE = 'Not a context variable';

/** The quiet line when Xano refuses a team write (`edit_prompts`, #595). */
export const TEAM_FORBIDDEN =
  'Only admins and managers can change team prompts';

/** A 403 from Xano: the caller lacks `edit_prompts`. */
export const isForbidden = (error: unknown) =>
  error instanceof AxiosError && error.response?.status === 403;

export type DetectedVariable = {
  name: string;
  dotted: boolean;
  /** On VH3's list of Context Variables: filled by the chat, nothing to edit. */
  context: boolean;
};

export type VariableDetails = { description: string; default: string };

export type PromptDraft = {
  title: string;
  body: string;
  /** Prompt Group id; 0 is My prompts. */
  groupId: number;
  /** 1 Connie, 2 Tia, 4 Salma. */
  agentIds: number[];
  /** Description and default per name; names come from the body. */
  variables: Record<string, VariableDetails>;
};

/**
 * Every `{name}` in the body, each once, in order; dotted and Context
 * Variables flagged.
 */
export const detectVariables = (
  body: string,
  context: PromptContextName[] = []
): DetectedVariable[] => {
  const seen = new Set<string>();
  const found: DetectedVariable[] = [];
  for (const match of body.matchAll(SLOT)) {
    const name = match[1];
    if (seen.has(name)) continue;
    seen.add(name);
    found.push({
      name,
      dotted: name.includes('.'),
      context: isContextName(context, name),
    });
  }
  return found;
};

export const toDraft = (initial: Partial<PromptDraft> = {}): PromptDraft => ({
  title: initial.title ?? '',
  body: initial.body ?? '',
  groupId: initial.groupId ?? 0,
  agentIds: initial.agentIds?.length ? [...initial.agentIds] : [1],
  variables: { ...initial.variables },
});

export const draftFromTemplate = (
  template: PromptTemplateRecord
): PromptDraft =>
  toDraft({
    title: template.title,
    body: template.body,
    groupId: template.group?.id ?? template.group_id ?? 0,
    agentIds: template.agent_ids,
    variables: Object.fromEntries(
      (template.variables ?? []).map(variable => [
        variable.name,
        {
          description: variable.description ?? '',
          default: variable.default ?? '',
        },
      ])
    ),
  });

const SUGGESTED_WORDS = 6;

/** The first six words of a sent message, trimmed to 80, with "…" when cut. */
export const suggestTitle = (text: string) => {
  const words = text.trim().split(/\s+/).filter(Boolean);
  let title = words.slice(0, SUGGESTED_WORDS).join(' ');
  let cut = words.length > SUGGESTED_WORDS;
  if (title.length > TITLE_MAX - 1) {
    title = title.slice(0, TITLE_MAX - 1).trimEnd();
    cut = true;
  }
  return cut ? `${title}…` : title;
};

/** The closest name on the list to a mistyped one, if any is near enough. */
export const closestContextName = (
  context: PromptContextName[],
  name: string
): string | undefined => {
  const wanted = name.toLowerCase();
  let best: { name: string; distance: number } | undefined;
  for (const item of context) {
    const distance = editDistance(wanted, item.name.toLowerCase());
    if (!best || distance < best.distance) best = { name: item.name, distance };
  }
  // Near enough to be a typo: a third of the name, at least two letters.
  return best && best.distance <= Math.max(2, Math.floor(wanted.length / 3))
    ? best.name
    : undefined;
};

function editDistance(a: string, b: string) {
  let previous = Array.from({ length: b.length + 1 }, (_, index) => index);
  for (let i = 1; i <= a.length; i++) {
    const row = [i];
    for (let j = 1; j <= b.length; j++) {
      row[j] = Math.min(
        previous[j] + 1,
        row[j - 1] + 1,
        previous[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
      );
    }
    previous = row;
  }
  return previous[b.length];
}

export type UnknownContextName = { name: string; suggestion?: string };

/**
 * Dotted names in the body that are not on the list, each once, with the
 * closest listed name as a suggestion. Empty while the list is empty: the
 * check waits for Xano then.
 */
export const unknownContextNames = (
  body: string,
  context: PromptContextName[]
): UnknownContextName[] =>
  context.length === 0
    ? []
    : detectVariables(body, context)
        .filter(variable => isUnknownDottedName(context, variable.name))
        .map(({ name }) => ({
          name,
          suggestion: closestContextName(context, name),
        }));

/** `{job.reference}, {job.type}, …`: the list as one line, for the refusal. */
export const contextNamesLine = (context: PromptContextName[]) =>
  context.map(item => `{${item.name}}`).join(', ');

/**
 * Why a Make variable name can't be used, or null when it can. A name on the
 * list makes a Context Variable; any other dotted name is refused.
 */
export const variableNameProblem = (
  name: string,
  context: PromptContextName[] = []
) => {
  if (name.includes('.')) {
    if (!DOTTED_NAME.test(name)) return 'Letters, numbers, - and _ only';
    return isContextName(context, name)
      ? null
      : `${NOT_CONTEXT_NOTE}. Use Insert context for those`;
  }
  if (!VARIABLE_NAME.test(name)) return 'Letters, numbers, - and _ only';
  return null;
};

/** The body with the range `start`–`end` replaced by `{name}`. */
export const makeVariable = (
  body: string,
  start: number,
  end: number,
  name: string
) => `${body.slice(0, start)}{${name}}${body.slice(end)}`;

export type DraftProblems = {
  title: boolean;
  body: boolean;
  /** A dotted name in the body that is not a Context Variable. */
  variables: boolean;
  agents: boolean;
};

export const draftProblems = (
  draft: PromptDraft,
  context: PromptContextName[] = []
): DraftProblems => {
  const title = draft.title.trim();
  const body = draft.body.trim();
  return {
    title: !title || draft.title.length > TITLE_MAX,
    body: !body || draft.body.length > BODY_MAX,
    variables: unknownContextNames(draft.body, context).length > 0,
    agents: draft.agentIds.length === 0,
  };
};

export const isDraftValid = (
  draft: PromptDraft,
  context: PromptContextName[] = []
) => !Object.values(draftProblems(draft, context)).some(Boolean);

/**
 * Description and default for each name in the body. Context Variables have
 * neither and are left out, as are dotted names (Xano reads the names itself).
 */
const variablesFor = (
  draft: PromptDraft,
  context: PromptContextName[]
): PromptVariableRecord[] =>
  detectVariables(draft.body, context)
    .filter(variable => !variable.dotted && !variable.context)
    .map(({ name }) => {
      const details = draft.variables[name];
      const description = details?.description.trim();
      const value = details?.default.trim();
      return {
        name,
        ...(description && { description }),
        ...(value && { default: value }),
      };
    });

/** A personal template, or a team one (#595) when `scope` says so. */
export const toCreateBody = (
  draft: PromptDraft,
  scope: 'personal' | 'team' = 'personal',
  context: PromptContextName[] = []
): CreatePromptTemplateBody => ({
  title: draft.title.trim(),
  body: draft.body,
  variables: variablesFor(draft, context),
  ...(draft.groupId ? { group_id: draft.groupId } : {}),
  agent_ids: [...draft.agentIds].sort((a, b) => a - b),
  ...(scope === 'team' && { scope }),
});

/**
 * Only what changed. Title, body and variables go together, since any of them
 * saves a new Template Version; group and agents edit the row.
 */
export const toUpdateBody = (
  draft: PromptDraft,
  from: PromptDraft,
  context: PromptContextName[] = []
): UpdatePromptTemplateBody => {
  const next = toCreateBody(draft, 'personal', context);
  const before = toCreateBody(from, 'personal', context);
  const contentChanged =
    next.title !== before.title ||
    next.body !== before.body ||
    JSON.stringify(next.variables) !== JSON.stringify(before.variables);
  return {
    ...(contentChanged && {
      title: next.title,
      body: next.body,
      variables: next.variables,
    }),
    ...(draft.groupId !== from.groupId && { group_id: draft.groupId }),
    ...(JSON.stringify(next.agent_ids) !== JSON.stringify(before.agent_ids) && {
      agent_ids: next.agent_ids,
    }),
  };
};

export const sameDraft = (
  a: PromptDraft,
  b: PromptDraft,
  context: PromptContextName[] = []
) =>
  JSON.stringify(toCreateBody(a, 'personal', context)) ===
  JSON.stringify(toCreateBody(b, 'personal', context));

export type ServerErrorField = 'title' | 'body' | 'variables' | 'form';

const FIELDS: ServerErrorField[] = ['title', 'body', 'variables'];

/** The Xano error as a line under the field it names, else under the footer. */
export const serverError = (
  error: unknown
): { field: ServerErrorField; message: string } => {
  const data =
    error instanceof AxiosError
      ? (error.response?.data as
          | { message?: string; payload?: { param?: string } }
          | undefined)
      : undefined;
  const message =
    data?.message?.trim() || 'Couldn’t save the prompt. Try again.';
  // Xano's refusal lists every name (`{document.title}` among them), so it
  // is matched by its start, not by the words it contains.
  if (message.startsWith(NOT_CONTEXT_NOTE)) {
    return { field: 'variables', message };
  }
  const param = data?.payload?.param?.toLowerCase() ?? '';
  const named =
    FIELDS.find(field => param.startsWith(field.replace(/s$/, ''))) ??
    FIELDS.find(field =>
      message.toLowerCase().includes(field.replace(/s$/, ''))
    );
  return { field: named ?? 'form', message };
};
